import { useState, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import useGlobal from "../store/store";
import { useNavigate } from 'raviger';
import { feathersClient } from "../api/client";
import { getChildrenListAPI } from "api/api";
import { system } from "../api/client";

const useAuth0Feathers = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const {
        loginWithRedirect,
        isAuthenticated,
        loginWithPopup,
        isLoading,
        getAccessTokenSilently,
        logout: auth0Logout
    } = useAuth0();

    const [isLoggedIn, setIsLoggedIn] = useGlobal(
        (state) => state.isLoggedIn,
        (actions) => actions.setIsLoggedIn
    );
    const [guestPayment, setGuestPayment] = useGlobal(
        (state) => state.guestPayment,
        (actions) => actions.setGuestPayment
    );
    const [emailExsist, setEmailExsist] = useGlobal(
        state => state.emailExsist,
        actions => actions.setEmailExsist
    );
    const [emailEmpty, setEmailEmpty] = useGlobal(
        state => state.emailEmpty,
        actions => actions.setEmailEmpty
    );
    const [user, setUser] = useGlobal(
        (state) => state.user,
        (actions) => actions.setUser
    );

    const handleEmailExsist = () => {
        navigate('/');
        setEmailExsist(true);
    };

    const handleEmailEmpty = () => {
        navigate('/');
        setEmailEmpty(true);
    };

    /**
     * Authenticate with Feathers using Auth0 token
     */
    const authenticateWithAuth0 = useCallback(async (username = null, phoneNumber = null) => {
        setLoading(true);
        setError(null);

        try {
            console.log('🔐 Starting Feathers authentication with Auth0...');

            // Get Auth0 access token
            const accessToken = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env[`REACT_APP_AUTH0_AUDIENCE_${system.toUpperCase()}`],
                    scope: "openid profile email offline_access"
                }
            });

            console.log('✅ Got Auth0 token:', accessToken ? accessToken.substring(0, 30) + '...' : 'NO TOKEN!');

            if (!accessToken) {
                throw new Error('No access token received from Auth0');
            }

            // ⬇️ IMPORTANT: Authenticate with Feathers using auth0 strategy
            console.log('📞 Calling Feathers /authentication with token...');

            const authenticatedUser = await feathersClient.authenticate({
                strategy: 'auth0',
                access_token: accessToken,  // ⬅️ Make sure this is included!
                username,
                phoneNumber
            });

            console.log('✅ Feathers authentication successful!');
            console.log('   User:', authenticatedUser?.email);

            setIsLoggedIn(true);

            // Determine role
            let role;
            if (guestPayment) {
                role = "guestPayment";
            } else {
                role = authenticatedUser?.email?.includes('zofim.org.il') ? 'tribe' : 'parent';
            }

            console.log('🎭 User role:', role);

            authenticatedUser.role = role;
            setUser(authenticatedUser);

            // Navigate based on role and state
            if (!authenticatedUser?.hasChildren && role === 'parent' && authenticatedUser?.email_verified) {
                console.log('📍 Navigating to: /register/addchild');
                navigate('/register/addchild');
            } else {
                switch (role) {
                    case 'tribe':
                        console.log('📍 Navigating to: /tribe');
                        navigate('/tribe');
                        break;
                    case 'parent':
                        const destination = authenticatedUser?.email_verified || authenticatedUser?.createdFromSAP
                            ? '/parent'
                            : `/register/mailver/${authenticatedUser?.email}`;
                        console.log('📍 Navigating to:', destination);
                        navigate(destination);
                        break;
                    case 'guestPayment':
                        console.log('📍 Navigating to:', guestPayment);
                        navigate(guestPayment);
                        break;
                    default:
                        break;
                }
            }

            return authenticatedUser;

        } catch (e) {
            console.error('❌ Auth0 Feathers authentication error:', e);
            console.error('   Error details:', {
                message: e.message,
                code: e.code,
                data: e.data
            });

            if (e.data?.emailExsist) {
                handleEmailExsist();
            } else if (e.data?.emailEmpty) {
                handleEmailEmpty();
            }

            setIsLoggedIn(false);
            setError(e);
            throw e;
        } finally {
            setLoading(false);
        }
    }, [getAccessTokenSilently, guestPayment, navigate, setIsLoggedIn, setUser]);

    /**
     * Re-authenticate on app load
     */
    /**
     * Re-authenticate on app load using Feathers token from localStorage
     */
    const reAuthenticate = useCallback(async () => {
        setLoading(true);

        try {
            console.log('🔄 Re-authenticating from localStorage...');

            // Try to reAuthenticate with Feathers directly
            // This will use the JWT token stored in localStorage
            const authResult = await feathersClient.reAuthenticate();

            console.log('✅ Feathers re-authentication successful');

            // Normalize - user might be at top level or nested
            const authenticatedUser = authResult.user || authResult;

            let role;
            if (guestPayment) {
                role = "guestPayment";
            } else {
                // Use the correct path to email
                const userEmail = authenticatedUser.email || authResult.user?.email;
                console.log('🔍 Re-authenticated user email:', userEmail);
                role = userEmail?.includes('zofim.org.il') ? 'tribe' : 'parent';
            }

            // Create normalized structure
            const normalizedResult = {
                ...authResult,
                user: authenticatedUser,
                role: role
            };

            setUser(normalizedResult);
            setIsLoggedIn(true);

            const hash = window.location.pathname;

            console.log('📍 Current path:', hash);
            console.log('🎭 User role:', role);

            // Navigate based on role and current path
            switch (role) {
                case 'tribe':
                    if (hash === '/') navigate('/tribe');
                    break;
                case 'parent':
                    try {
                        const parent = await getChildrenListAPI();
                        const hasSapChildren = parent.d.results[0].ChildrenList.results.length > 0;

                        if (!authenticatedUser.hasChildren && !hasSapChildren) {
                            navigate('/register/addchild');
                            break;
                        }

                        if (hash === '/') {
                            navigate('/parent');
                        }
                    } catch (error) {
                        console.log('⚠️ Could not fetch children:', error);
                        if (hash === '/') {
                            navigate('/parent');
                        }
                    }
                    break;
                case 'guestPayment':
                    navigate(guestPayment);
                    break;
                default:
                    break;
            }

            return normalizedResult;

        } catch (e) {
            console.log('ℹ️ No stored authentication session');
            setIsLoggedIn(false);
            throw e;
        } finally {
            setLoading(false);
        }
    }, [guestPayment, navigate, setIsLoggedIn, setUser]); // ⬅️ Removed isAuthenticated dependency
    /**
     * Handle successful authentication and navigate
     * Centralized navigation logic for all login methods
     */
    const handleSuccessfulLogin = useCallback(async (authResult, username = null) => {
        console.log('✅ Handling successful login...');

        // Normalize - user might be at top level or nested
        const authenticatedUser = authResult.user || authResult;

        console.log('   User:', authenticatedUser?.email);
        console.log('   Email verified:', authenticatedUser?.email_verified);
        console.log('   Has children:', authenticatedUser?.hasChildren);

        // Set logged in state
        setIsLoggedIn(true);

        // Determine role
        let role;
        if (guestPayment) {
            role = "guestPayment";
        } else {
            role = authenticatedUser?.email?.includes('zofim.org.il') ? 'tribe' : 'parent';
        }

        console.log('🎭 User role:', role);

        // Create full user object
        const fullUser = {
            ...authResult,
            user: authenticatedUser,
            role: role
        };

        setUser(fullUser);

        // Navigate based on role and user state
        if (!authenticatedUser?.hasChildren && role === 'parent' && authenticatedUser?.email_verified) {
            console.log('📍 Navigating to: /register/addchild');
            navigate('/register/addchild');
        } else {
            switch (role) {
                case 'tribe':
                    console.log('📍 Navigating to: /tribe');
                    navigate('/tribe');
                    break;
                case 'parent':
                    const destination = authenticatedUser?.email_verified || authenticatedUser?.createdFromSAP || authResult?.createdFromSAP
                        ? '/parent'
                        : `/register/mailver/${authenticatedUser?.email}`;
                    console.log('📍 Navigating to:', destination);
                    navigate(destination);
                    break;
                case 'guestPayment':
                    console.log('📍 Navigating to:', guestPayment);
                    navigate(guestPayment);
                    break;
                default:
                    break;
            }
        }

        return fullUser;
    }, [guestPayment, navigate, setIsLoggedIn, setUser]);
    /**
     * Login with Office 365 (using Auth0)
     */
    const loginWithOffice365 = useCallback(async () => {
        setLoading(true);
        try {
            console.log('🔵 Starting Office 365 popup login...');

            await loginWithPopup({
                authorizationParams: {
                    connection: 'zofim-azure-ad',
                    scope: 'openid profile email offline_access',
                    audience: process.env[`REACT_APP_AUTH0_AUDIENCE_${system.toUpperCase()}`],
                }
            });

            console.log('✅ Popup login successful, now authenticating with Feathers...');

            // ✅ Small delay to let Auth0 session settle before getAccessTokenSilently fires
            await new Promise(resolve => setTimeout(resolve, 500));

            await authenticateWithAuth0();

            console.log('✅ Full authentication complete!');

        } catch (error) {
            console.error('❌ Office 365 login error:', error);
            setError(error);
            setLoading(false);
            throw error;
        }
    }, [loginWithPopup, authenticateWithAuth0]);

    /**
     * Login with email/password using Auth0
     */
    const loginWithEmailPassword = useCallback(async (email, password) => {
        setLoading(true);
        try {
            await loginWithRedirect({
                authorizationParams: {
                    login_hint: email,
                    audience: process.env[`REACT_APP_AUTH0_AUDIENCE_${system.toUpperCase()}`],
                }
            });
        } catch (error) {
            console.error('Email/password login error:', error);
            setError(error);
            setLoading(false);
            throw error;
        }
    }, [loginWithRedirect]);

    /**
     * Logout from both Auth0 and Feathers
     */
    const logOut = useCallback(async () => {
        try {
            await feathersClient.logout();

            auth0Logout({
                logoutParams: {
                    returnTo: window.location.origin
                }
            });

            setIsLoggedIn(false);
            setUser({});
            window.localStorage.clear();
            navigate('/');
        } catch (e) {
            console.error('Logout error:', e);
        }
    }, [auth0Logout, navigate, setIsLoggedIn, setUser]);

    /**
     * Login anonymously using backend guest tokens
     * Replaces Firebase anonymous auth - does NOT use Auth0 (no MAU charges)
     * Used for temporary access to check username, reset password, etc.
     */
    const loginAnonymously = useCallback(async () => {
        try {
            console.log('🔓 Starting anonymous login with guest token...');

            // Get guest token from backend
            const guestTokenResponse = await feathersClient.service('guest-token').create({});

            console.log('✅ Guest token received:', guestTokenResponse.guestId);

            // Store token in feathersClient for subsequent requests
            await feathersClient.authentication.setAccessToken(guestTokenResponse.accessToken);

            console.log('✅ Anonymous login successful');

            return {
                guestToken: guestTokenResponse.accessToken,
                guestId: guestTokenResponse.guestId,
                anonymous: true,
                expiresIn: guestTokenResponse.expiresIn
            };
        } catch (error) {
            console.error('❌ Anonymous login failed:', error);
            setError(error);
            throw error;
        }
    }, []);

    /**
     * Logout from anonymous session
     * Clears the guest token from feathersClient
     */
    const logoutAnonymously = useCallback(async () => {
        try {
            console.log('🔓 Logging out from anonymous session...');

            // Clear the guest token from feathersClient
            await feathersClient.authentication.removeAccessToken();

            console.log('✅ Anonymous logout successful');
        } catch (error) {
            console.error('❌ Anonymous logout failed:', error);
        }
    }, []);

    const loginWithUsernamePassword = useCallback(async (username, password) => {
        setLoading(true);
        setError(null);

        try {
            console.log('🔐 Starting Auth0 username/password login...');
            console.log('   Username:', username);

            const backendUrl = process.env[`REACT_APP_BACKEND_URL_${system.toUpperCase()}`] || 'http://localhost:3030';

            console.log('📞 Calling backend auth0-login service...');

            const auth0Response = await fetch(`${backendUrl}/auth0-login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username, password })
            });

            const auth0Data = await auth0Response.json();

            if (!auth0Response.ok) {
                console.error('❌ Auth0 login failed:', auth0Data);

                // ⬅️ NEW: Handle password reset sent
                if (auth0Data.message === 'PASSWORD_RESET_SENT') {
                    const err = new Error('PASSWORD_RESET_SENT');
                    err.email = auth0Data.email;
                    throw err;
                } else if (auth0Data.message === 'INVALID_CREDENTIALS') {
                    throw new Error('INVALID_CREDENTIALS');
                } else if (auth0Data.message === 'ACCOUNT_BLOCKED') {
                    throw new Error('ACCOUNT_BLOCKED');
                } else {
                    throw new Error('AUTH_FAILED');
                }
            }

            const accessToken = auth0Data.access_token;
            console.log('✅ Got Auth0 token from backend');

            console.log('📞 Authenticating with Feathers backend...');

            const authResult = await feathersClient.authenticate({
                strategy: 'auth0',
                access_token: accessToken,
                username: username,
                phoneNumber: null
            });

            // ⬅️ NORMALIZE: User might be nested or at top level
            const authenticatedUser = authResult.user || authResult;

            console.log('✅ Full authentication complete!');
            console.log('   User:', authenticatedUser?.email);
            console.log('   Email verified:', authenticatedUser?.email_verified);
            console.log('   Created from SAP:', authResult?.createdFromSAP);

            setIsLoggedIn(true);

            // Determine role
            let role;
            if (guestPayment) {
                role = "guestPayment";
            } else {
                role = authenticatedUser?.email?.includes('zofim.org.il') ? 'tribe' : 'parent';
            }

            console.log('🎭 User role:', role);

            // Create full user object
            const fullUser = {
                ...authResult,
                user: authenticatedUser,
                role: role
            };

            setUser(fullUser);

            // Navigate based on role
            if (!authenticatedUser?.hasChildren && role === 'parent' && authenticatedUser?.email_verified) {
                console.log('📍 Navigating to: /register/addchild');
                navigate('/register/addchild');
            } else {
                switch (role) {
                    case 'tribe':
                        console.log('📍 Navigating to: /tribe');
                        navigate('/tribe');
                        break;
                    case 'parent':
                        const destination = authenticatedUser?.email_verified || authenticatedUser?.createdFromSAP || authResult?.createdFromSAP
                            ? '/parent'
                            : `/register/mailver/${authenticatedUser?.email}`;
                        console.log('📍 Navigating to:', destination);
                        navigate(destination);
                        break;
                    case 'guestPayment':
                        console.log('📍 Navigating to:', guestPayment);
                        navigate(guestPayment);
                        break;
                    default:
                        break;
                }
            }

            return fullUser;

        } catch (e) {
            console.error('❌ Username/password login error:', e);
            setIsLoggedIn(false);
            setError(e);
            throw e;
        } finally {
            setLoading(false);
        }
    }, [navigate, guestPayment, setIsLoggedIn, setUser]);


    return {
        loading: loading || isLoading,
        error,
        isLoggedIn,
        setIsLoggedIn,
        isAuthenticated,
        user,
        handleSuccessfulLogin,
        app: feathersClient,
        loginWithUsernamePassword,
        authenticateWithAuth0,
        reAuthenticate,
        loginWithOffice365,
        loginWithEmailPassword,
        logOut,
        getAccessTokenSilently,
        loginAnonymously,      // ✅ NEW: Guest token login
        logoutAnonymously      // ✅ NEW: Guest token logout
    };
};

export default useAuth0Feathers;